var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import AddIcon from '@mui/icons-material/Add';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { buildAssetUrl, assetSize } from 'root/src/client/v2/common/helpers/assetUrl';
import { DarePlatformIcon, PlatformIconColor } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import format2Decimals from 'root/src/shared/util/format2Decimals';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { Spinner } from 'root/src/client/v2/common/components/ui/spinner';
import { Popover, PopoverContent, PopoverTrigger } from 'root/src/client/v2/common/components/ui/popover';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { RedesignShareMenu } from 'root/src/client/v2/common/components/RedesignShareMenu';
import { RewardRemaining, isUncountableReward } from 'root/src/client/v2/common/components/RewardRemaining';
import { RewardAmountBadge } from 'root/src/client/v2/common/components/RewardAmountBadge';
import { RedesignDeliverDareModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignDeliverDareModal';
import { RedesignRejectDareModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignRejectDareModal';
import { RedesignDareDetailsModal } from 'root/src/client/v2/_pages/creator/redesign/RedesignDareDetailsModal';
import { useCreatorClips, useCreatorClipCounts } from 'root/src/client/v2/_pages/creator/redesign/useCreatorClips';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Img } from 'root/src/client/v2/common/components/ui/image';
const TABS = [
    { label: 'All', value: 'all' },
    { label: 'Completed', value: 'completed' },
    { label: 'Active', value: 'active' },
];
const getStatusBadge = (status) => {
    if (status === 'projectDelivered') {
        return { label: 'Completed', classes: 'border-neon-green/40 text-neon-green' };
    }
    if (status === 'deliveryPending') {
        return { label: 'In Review', classes: 'border-neon-amber/40 text-neon-amber' };
    }
    return { label: 'Active', classes: 'border-neon-pink/40 text-neon-pink' };
};
const STATUS_NOT_DELIVERABLE = ['projectDelivered', 'rejected', 'deliveryRejected'];
const ClipMenuItem = ({ icon, label, onClick, destructive }) => (<button type="button" onClick={onClick} className={cn('flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors', destructive
        ? 'bg-destructive/10 text-destructive hover:bg-destructive/20'
        : 'text-foreground hover:bg-white/[0.06]')}>
		<span className={cn('flex', destructive ? 'text-destructive' : 'text-muted-foreground')}>{icon}</span>
		{label}
	</button>);
const ClipCardMenu = ({ clip, onChanged }) => {
    const [open, setOpen] = useState(false);
    const redesignModal = useRedesignModal();
    const onDeliver = () => {
        var _a;
        setOpen(false);
        redesignModal.show(<RedesignDeliverDareModal projectUrl={(_a = clip.projectUrl) !== null && _a !== void 0 ? _a : ''} onSuccess={onChanged}/>);
    };
    const onReject = () => {
        var _a;
        setOpen(false);
        redesignModal.show(<RedesignRejectDareModal projectUrl={(_a = clip.projectUrl) !== null && _a !== void 0 ? _a : ''} onSuccess={onChanged}/>);
    };
    return (<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button type="button" aria-label="Dare options" className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground">
					<MoreVertIcon sx={{ fontSize: 18 }}/>
				</button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-44 p-1">
				<ClipMenuItem icon={<SendOutlinedIcon sx={{ fontSize: 16 }}/>} label="Deliver" onClick={onDeliver}/>
				<ClipMenuItem icon={<CancelOutlinedIcon sx={{ fontSize: 16 }}/>} label="Reject" onClick={onReject} destructive/>
			</PopoverContent>
		</Popover>);
};
const ClipCard = ({ clip, isOwnProfile, onChanged }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const game = (_a = clip.games) === null || _a === void 0 ? void 0 : _a[0];
    const assignee = (_b = clip.assignees) === null || _b === void 0 ? void 0 : _b[0];
    const deliveryThumbnail = (_d = (_c = clip.deliveries) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.thumbnail;
    const title = clip.title || (game === null || game === void 0 ? void 0 : game.name) || 'Dare';
    const thumbnail = deliveryThumbnail
        ? buildAssetUrl(deliveryThumbnail, { w: assetSize('mobile'), q: 80, fmt: 'webp' })
        : (_e = game === null || game === void 0 ? void 0 : game.boxArtTemplateUrl) === null || _e === void 0 ? void 0 : _e.replace('{width}x{height}', '600x800');
    const avatar = buildAssetUrl(assignee === null || assignee === void 0 ? void 0 : assignee.image, { w: assetSize('thumbnail'), q: 80, fmt: 'webp' });
    const badge = getStatusBadge(clip.status);
    const detailsModal = useRedesignModal();
    const creatorName = assignee === null || assignee === void 0 ? void 0 : assignee.displayName;
    const platform = assignee === null || assignee === void 0 ? void 0 : assignee.platform;
    const reduceMotion = useReducedMotion();
    const pledgeAmount = (_f = clip.pledgeAmount) !== null && _f !== void 0 ? _f : 0;
    const goalAmount = ((_g = clip.assignees) !== null && _g !== void 0 ? _g : []).reduce((sum, current) => { var _a; return sum + ((_a = current.amountRequested) !== null && _a !== void 0 ? _a : 0); }, 0);
    const hasReward = pledgeAmount > 0 || goalAmount > 0;
    const rewardProgress = goalAmount > 0 ? Math.min(1, pledgeAmount / goalAmount) : (pledgeAmount > 0 ? 1 : 0);
    const shareLink = clip.shareUrl || clip.projectUrl;
    const canManage = Boolean(isOwnProfile) && !STATUS_NOT_DELIVERABLE.includes((_h = clip.status) !== null && _h !== void 0 ? _h : '');
    // Active (not-yet-delivered) dares on your own profile get a prominent "Deliver clip" CTA; In Review /
    // completed / rejected keep only the kebab (re-deliver / reject).
    const canDeliver = canManage && clip.status !== 'deliveryPending';
    // Pledge mirrors the legacy ProjectCardButton gate: a monetary (non brand-product) dare that is not
    // delivered/rejected and where the viewer is not the assignee (i.e. not your own profile). Enters the
    // existing legacy pledge flow for this specific dare (full navigation, legacy route).
    const canPledge = !isOwnProfile
        && !clip.brandProduct
        && Boolean(clip.projectUrl)
        && !STATUS_NOT_DELIVERABLE.includes((_j = clip.status) !== null && _j !== void 0 ? _j : '');
    const openDetails = () => detailsModal.show(<RedesignDareDetailsModal clip={clip} isOwnProfile={isOwnProfile} onChanged={onChanged}/>);
    const onDeliver = () => { var _a; return detailsModal.show(<RedesignDeliverDareModal projectUrl={(_a = clip.projectUrl) !== null && _a !== void 0 ? _a : ''} onSuccess={onChanged}/>); };
    const thumbnailNode = (<div className="relative aspect-video w-full overflow-hidden bg-secondary">
			{thumbnail && (<Img src={thumbnail} alt={title} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"/>)}
			<div className="absolute inset-0 flex items-center justify-center bg-background/30 opacity-0 transition-opacity group-hover:opacity-100">
				<span className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-neon-pink/80 to-neon-purple/80 text-white backdrop-blur-sm">
					<PlayArrowRoundedIcon />
				</span>
			</div>
			<span className={cn('absolute left-3 top-3 inline-flex items-center rounded-lg border border-solid bg-background/70 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider backdrop-blur-md', badge.classes)}>
				{badge.label}
			</span>
		</div>);
    return (<div className="glass group flex h-full flex-col overflow-hidden rounded-2xl border border-solid border-white/[0.08] transition-colors hover:border-neon-pink/30">
			<button type="button" onClick={openDetails} className="block w-full">{thumbnailNode}</button>
			<div className="flex flex-1 flex-col p-4">
				<div className="mb-2 flex items-center gap-2">
					<div className="relative shrink-0">
						{avatar
            ? <Img src={avatar} alt={creatorName !== null && creatorName !== void 0 ? creatorName : 'Creator'} className="h-7 w-7 rounded-full object-cover ring-1 ring-white/10"/>
            : <div className="h-7 w-7 rounded-full bg-secondary"/>}
						{platform && (<span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-white">
								<DarePlatformIcon size="10" platform={platform} color={PlatformIconColor.Dark}/>
							</span>)}
					</div>
					<span className="flex-1 truncate text-xs text-muted-foreground">{(_k = creatorName !== null && creatorName !== void 0 ? creatorName : game === null || game === void 0 ? void 0 : game.name) !== null && _k !== void 0 ? _k : 'Creator'}</span>
					<div className="flex shrink-0 items-center gap-0.5">
						{shareLink && (<RedesignShareMenu url={shareLink} className="h-7 w-7 rounded-md border-transparent bg-transparent text-muted-foreground backdrop-blur-none hover:bg-white/[0.06] hover:text-foreground"/>)}
						{canManage && <ClipCardMenu clip={clip} onChanged={onChanged}/>}
					</div>
				</div>
				<button type="button" onClick={openDetails} className="block w-full truncate text-left font-display text-sm font-bold text-foreground transition-colors hover:text-neon-pink">
					{title}
				</button>
				{clip.description && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{clip.description}</p>}
				{(game === null || game === void 0 ? void 0 : game.name) && <p className="mt-1.5 truncate text-[11px] uppercase tracking-wide text-muted-foreground/70">{game.name}</p>}
				{((_l = clip.brandProduct) === null || _l === void 0 ? void 0 : _l.name) && (<div className="mt-3 flex items-start gap-2 border-t border-solid border-white/[0.06] pt-3">
						<CardGiftcardIcon sx={{ fontSize: 15 }} className="mt-0.5 shrink-0 text-neon-green"/>
						<div className="min-w-0 flex-1">
							<div className="flex items-start justify-between gap-2">
								<p className="min-w-0 truncate text-xs font-medium text-foreground/90">{clip.brandProduct.name}</p>
								{typeof clip.brandProduct.amount === 'number' && <RewardAmountBadge amount={clip.brandProduct.amount}/>}
							</div>
							<RewardRemaining capacity={clip.capacity} isUnlimited={isUncountableReward(clip.brandProduct.type)} className="mt-1.5"/>
						</div>
					</div>)}
				{hasReward && (<div className="mt-3">
						<div className="mb-1 flex items-center justify-between text-[11px]">
							<span className="font-semibold text-foreground">
								${format2Decimals(pledgeAmount)}{goalAmount ? ` of $${format2Decimals(goalAmount)}` : ''}
							</span>
							<span className="uppercase tracking-wide text-muted-foreground/70">Reward</span>
						</div>
						<div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
							<motion.div className="h-full rounded-full bg-gradient-to-r from-neon-pink to-neon-purple" initial={{ width: 0 }} animate={{ width: `${rewardProgress * 100}%` }} transition={{ duration: reduceMotion ? 0 : 0.6, ease: 'easeOut' }}/>
						</div>
					</div>)}
				{canDeliver && (<div className="mt-auto pt-5">
						<Button variant="brand" onClick={onDeliver} className="w-full rounded-lg text-xs font-semibold">
							<SendOutlinedIcon sx={{ fontSize: 14 }}/>
							Deliver clip
						</Button>
					</div>)}
				{canPledge && (<div className="mt-auto pt-5">
						<Button variant="brand" asChild className="w-full rounded-lg text-xs font-semibold">
							<a href={`/pledge-project/${clip.projectUrl}`}>
								<PaidOutlinedIcon sx={{ fontSize: 14 }}/>
								Pledge to this dare
							</a>
						</Button>
					</div>)}
			</div>
		</div>);
};
export const CreatorDareClips = memo(({ recordId, isOwnProfile, createProjectHref = '/create-project' }) => {
    const [tab, setTab] = useState('all');
    const reduceMotion = useReducedMotion();
    const { clips, isLoading, isError, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } = useCreatorClips(recordId, tab);
    const { counts, refetch: refetchCounts } = useCreatorClipCounts(recordId);
    const onChanged = () => __awaiter(void 0, void 0, void 0, function* () { yield Promise.all([refetch(), refetchCounts()]); });
    return (<section className="mt-10">
			<div className="mb-6 flex flex-wrap items-center justify-between gap-3">
				<div className="flex w-fit items-center gap-1 rounded-xl bg-secondary/50 p-1">
					{TABS.map((option) => {
            const active = tab === option.value;
            const count = counts[option.value];
            return (<button key={option.value} type="button" onClick={() => setTab(option.value)} className={cn('relative rounded-lg px-4 py-2 text-sm font-medium transition-colors', active ? 'text-white' : 'text-muted-foreground hover:text-foreground')}>
								{active && (<motion.span layoutId="creatorClipsTab" className="absolute inset-0 rounded-lg bg-gradient-to-r from-neon-pink to-neon-purple shadow-sm" transition={reduceMotion ? { duration: 0 } : { type: 'spring', bounce: 0.2, duration: 0.4 }}/>)}
								<span className="relative z-10 flex items-center gap-1.5">
									{option.label}
									{typeof count === 'number' && (<span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none tabular-nums transition-colors', active ? 'bg-white/20 text-white' : 'bg-white/[0.08] text-muted-foreground')}>
											{count}
										</span>)}
								</span>
							</button>);
        })}
				</div>

				{/* Mirrors the legacy "Create a new Dare +" banner CTA that sat above the creator's
        projects list. Always shown; navigates to the legacy create-project flow (full nav,
        legacy route). Seeded with the creator via query param where autoPopulateDare can read
        it (twitch/youtube). */}
				<Button variant="brand" size="sm" asChild className="rounded-xl text-xs font-semibold uppercase tracking-wide">
					<a href={createProjectHref}>
						<AddIcon sx={{ fontSize: 16 }}/>
						Create project
					</a>
				</Button>
			</div>

			{isLoading && (<div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
					{['a', 'b', 'c', 'd'].map(id => (<Skeleton key={id} className="aspect-video w-full rounded-2xl"/>))}
				</div>)}

			{!isLoading && (isError || clips.length === 0) && (<div className="glass flex flex-col items-center justify-center rounded-2xl border border-solid border-white/[0.08] py-16">
					<PlayArrowRoundedIcon className="mb-2 text-neon-pink/40" sx={{ fontSize: 32 }}/>
					<p className="text-sm text-muted-foreground">No dare clips in this category yet.</p>
				</div>)}

			{!isLoading && !isError && clips.length > 0 && (
        // @ts-ignore react-infinite-scroller children typing
        <InfiniteScroll pageStart={1} threshold={800} loadMore={() => { if (!isFetchingNextPage) {
            fetchNextPage();
        } }} hasMore={Boolean(hasNextPage)} loader={(<div key="loader" className="flex w-full justify-center py-6">
							<Spinner />
						</div>)}>
					<motion.div layout className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
						<AnimatePresence mode="popLayout">
							{clips.map((clip, index) => {
                var _a, _b;
                return (<motion.div key={(_b = (_a = clip.id) !== null && _a !== void 0 ? _a : clip.projectUrl) !== null && _b !== void 0 ? _b : clip.title} layout className="h-full" initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.3, delay: Math.min(index * 0.05, 0.3), layout: { duration: 0.3 } }}>
									<ClipCard clip={clip} isOwnProfile={isOwnProfile} onChanged={onChanged}/>
								</motion.div>);
            })}
						</AnimatePresence>
					</motion.div>
				</InfiniteScroll>)}
		</section>);
});
